import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useNavigate, useParams } from 'react-router';

import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { OrderReportType, PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { orderReportName } from '@/shared/i18n';
import { trackEvent } from '@/shared/lib';
import { Button } from '@/shared/ui';

import { billingModel, CoinBadge, DiagnocoinFunding } from '@/entities/billing';
import {
  composedModel,
  useTranscriptorStartAvailability,
} from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import {
  organizationModel,
  useStorageBlockedTooltip,
} from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { orderReportSelectors } from '@/features/orderReport';

import { ORDER_REPORT_SERVICE_TYPES } from '../../config';

import styles from './OrderReport.module.scss';

type OrderReportProps = {
  modal?: boolean;
};
const selectOrderReportServices =
  billingModel.selectors.selectActiveServiceValuesByTypes(
    ORDER_REPORT_SERVICE_TYPES,
  );

export const OrderReport = (props: OrderReportProps) => {
  const { modal } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { patientID } = useParams();

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const activeServices = useAppSelector(selectOrderReportServices);

  const cbctService =
    activeServices[ServiceType.ServiceType_Order_CBCT_GP_Analysis];
  const panoService =
    activeServices[ServiceType.ServiceType_Order_Panoramic_GP_Analysis];
  const ioxrayService =
    activeServices[ServiceType.ServiceType_Order_IOXRay_GP_Analysis];
  const cbctStlService =
    activeServices[ServiceType.ServiceType_Order_CBCT_STL_Analysis];
  const cbctIOSSuperimpositionService =
    activeServices[
      ServiceType.ServiceType_Order_CBCT_IOS_Superimposition_Analysis
    ];
  const implantStudioService =
    activeServices[ServiceType.ServiceType_Order_ImplantStudio_Analysis];
  const orthoStudioService =
    activeServices[ServiceType.ServiceType_Order_OrthoStudio_Analysis];
  const cbctOrthodonticService =
    activeServices[ServiceType.ServiceType_Order_CBCT_Orthodontic_Analysis];
  const iosSegmentationService =
    activeServices[ServiceType.ServiceType_Order_IOS_Segmentation_Analysis];
  const enableUploadDentalPhotoService =
    activeServices[ServiceType.ServiceType_Enable_UploadDentalPhoto];

  const showIOSReportButton = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_IOSReportButton,
    ),
  );

  const enableOrthoStudio = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_OrthoStudio,
    ),
  );

  const enablePhotosFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_DentalPhotosWidget,
    ),
  );

  const isForbidUploadsFromWeb = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
    ),
  );

  const buttonsVisibility = useAppSelector(
    orderReportSelectors.selectOrderReportButtonsVisibility,
  );

  const enableAgentChatFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_AgentChat,
    ),
  );
  const { CanEditPatient: canEditDocuments } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID || ''),
  );
  const shouldShowNewDocumentButton =
    enableAgentChatFeature && canEditDocuments;

  const handleNewDocumentClick = () => {
    trackEvent({
      eventName: 'PatientProfilePageNewDocumentButtonClicked',
    });

    navigate(
      `${generatePath(PATHS.patient, { patientID: patientID || '' })}/documents/new?documentMode=edit&documentPane=document`,
    );
  };

  const {
    funding: transcriptorFunding,
    canStartRecording: shouldShowTranscriptorButton,
    isStartRecordingDisabled,
    isStorageBlocked,
  } = useTranscriptorStartAvailability();

  const { wrapWithStorageTooltip } = useStorageBlockedTooltip({
    isStorageBlocked,
    triggerClassName: styles.tooltipTrigger,
  });

  const {
    CBCT: cbctFunding,
    Pano: panoFunding,
    IOXRay: ioxrayFunding,
    '3DStudio': studio3dFunding,
    ImplantStudio: implantStudioFunding,
    OrthoStudio: orthoStudioFunding,
    IOSSegmentation: iosSegmentationFunding,
  } = useAppSelector(
    composedModel.selectors.selectDiagnocoinFundingByOrderReportType,
  );

  const isVisibleWithCoins = (funding: DiagnocoinFunding) =>
    funding.source === 'diagnocoins';

  const renderCoinBadge = (funding: DiagnocoinFunding) =>
    funding.source === 'diagnocoins' && (
      <CoinBadge className={styles.coinBadge} />
    );

  const handleOrderReportClick = (reportType: OrderReportType) => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.OrderReport,
        data: { reportType },
      }),
    );
  };

  const handleTranscriptorClick = () => {
    trackEvent({
      eventName: 'PatientProfilePageTranscriptorOrderReportButtonClicked',
    });

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.TranscriptorRecording,
        data: { patientID: patientID || '' },
      }),
    );
  };

  const handleDentalPhotoClick = () => {
    trackEvent({
      eventName: 'PatientProfilePageDentalPhotoOrderReportButtonClicked',
    });

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.DentalPhotosUpload,
        data: {
          scrollToWidgetOnUpload: true,
        },
      }),
    );
  };

  const handleMobileDentalPhotoUploadClick = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MobileDentalPhotoUploadMenu,
        data: { patientID: patientID ?? '' },
      }),
    );
  };

  // WA-6209: uploads are no longer metered/entitled, so there is no
  // CBCT/STL upload-capability check anymore.
  const hasRequiredServicesForOrthoStudio = true;
  const shouldShowCbctButton =
    (cbctService !== undefined || isVisibleWithCoins(cbctFunding)) &&
    buttonsVisibility.cbct;
  const shouldShowPanoButton =
    (panoService !== undefined || isVisibleWithCoins(panoFunding)) &&
    buttonsVisibility.pano;
  const shouldShowIOXRayButton =
    (ioxrayService !== undefined || isVisibleWithCoins(ioxrayFunding)) &&
    buttonsVisibility.ioxray;
  const shouldShow3DStudioButton =
    (cbctStlService !== undefined ||
      cbctIOSSuperimpositionService !== undefined ||
      isVisibleWithCoins(studio3dFunding)) &&
    buttonsVisibility.studio3d;
  const shouldShowImplantStudioButton =
    (implantStudioService !== undefined ||
      isVisibleWithCoins(implantStudioFunding)) &&
    buttonsVisibility.implant;
  const shouldShowOrthoStudioButton =
    (orthoStudioService !== undefined ||
      cbctOrthodonticService !== undefined ||
      isVisibleWithCoins(orthoStudioFunding) ||
      (enableOrthoStudio && hasRequiredServicesForOrthoStudio)) &&
    buttonsVisibility.ortho;
  const shouldShowIOSSegmentationButton =
    showIOSReportButton &&
    (iosSegmentationService !== undefined ||
      isVisibleWithCoins(iosSegmentationFunding)) &&
    buttonsVisibility.iosSegmentation;
  const shouldShowDentalPhotoButton =
    (enablePhotosFeature || enableUploadDentalPhotoService !== undefined) &&
    buttonsVisibility.dentalPhoto;
  // WA-6656: uploads are no longer metered, so the Intraoral 3D Scan button is
  // shown only when the billing plan includes a report type that consumes
  // intraoral scan files (Superimposition, Implant Studio, Ortho Studio,
  // IOS Segmentation).
  const shouldShowRawMeshViewerButton =
    (cbctIOSSuperimpositionService !== undefined ||
      implantStudioService !== undefined ||
      orthoStudioService !== undefined ||
      iosSegmentationService !== undefined) &&
    buttonsVisibility.rawMeshViewer;
  const visibleButtonsCount = [
    shouldShowCbctButton,
    shouldShowPanoButton,
    shouldShowIOXRayButton,
    shouldShow3DStudioButton,
    shouldShowImplantStudioButton,
    shouldShowOrthoStudioButton,
    shouldShowIOSSegmentationButton,
    shouldShowRawMeshViewerButton,
    shouldShowDentalPhotoButton,
    shouldShowTranscriptorButton,
    shouldShowNewDocumentButton,
  ].filter(Boolean).length;

  return (
    <>
      {!modal && (
        <div className={styles.header}>
          <h4 className={cn(styles.title, 'h4')}>
            <FormattedMessage
              id="orderReport.title"
              defaultMessage="Create AI report"
            />
          </h4>
        </div>
      )}

      <div
        className={cn(styles.ordersWrapper, modal && styles.modal)}
        data-count={visibleButtonsCount}
        data-section="create-report"
      >
        {shouldShowCbctButton &&
          wrapWithStorageTooltip(
            <Button
              data-component="create-cbct-report-button"
              onClick={() => handleOrderReportClick('CBCT')}
              variant="secondary"
              icon="cbct3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked || cbctFunding.source === 'unavailable'
              }
            >
              {renderCoinBadge(cbctFunding)}
              {formatMessage(orderReportName['CBCT'])}
            </Button>,
          )}

        {shouldShowPanoButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('Pano')}
              variant="secondary"
              icon="pano3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked || panoFunding.source === 'unavailable'
              }
            >
              {renderCoinBadge(panoFunding)}
              {formatMessage(orderReportName['Pano'])}
            </Button>,
          )}

        {shouldShowIOXRayButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('IOXRay')}
              variant="secondary"
              icon="ios3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked || ioxrayFunding.source === 'unavailable'
              }
            >
              {renderCoinBadge(ioxrayFunding)}
              {formatMessage(orderReportName['IOXRay'])}
            </Button>,
          )}

        {shouldShow3DStudioButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('3DStudio')}
              variant="secondary"
              icon="model3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked || studio3dFunding.source === 'unavailable'
              }
            >
              {renderCoinBadge(studio3dFunding)}
              {formatMessage(orderReportName['3DStudio'])}
            </Button>,
          )}

        {shouldShowImplantStudioButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('ImplantStudio')}
              variant="secondary"
              icon="implant3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked ||
                implantStudioFunding.source === 'unavailable'
              }
            >
              {renderCoinBadge(implantStudioFunding)}
              {formatMessage(orderReportName['ImplantStudio'])}
            </Button>,
          )}

        {shouldShowOrthoStudioButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('OrthoStudio')}
              variant="secondary"
              icon="ortho3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked ||
                (orthoStudioFunding.source === 'unavailable' &&
                  !enableOrthoStudio) ||
                !hasRequiredServicesForOrthoStudio
              }
            >
              {renderCoinBadge(orthoStudioFunding)}
              {formatMessage(orderReportName['OrthoStudio'])}
            </Button>,
          )}
        {shouldShowIOSSegmentationButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('IOSSegmentation')}
              variant="secondary"
              icon="model3d"
              className={styles.orderButton}
              disabled={
                isStorageBlocked ||
                iosSegmentationFunding.source === 'unavailable'
              }
            >
              {renderCoinBadge(iosSegmentationFunding)}
              {formatMessage(orderReportName['IOSSegmentation'])}
            </Button>,
          )}

        {shouldShowRawMeshViewerButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={() => handleOrderReportClick('RawMeshViewer')}
              variant="secondary"
              icon="model3d"
              className={styles.orderButton}
              disabled={isStorageBlocked}
            >
              {formatMessage(orderReportName['RawMeshViewer'])}
            </Button>,
          )}

        {shouldShowDentalPhotoButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={
                isMobile
                  ? handleMobileDentalPhotoUploadClick
                  : handleDentalPhotoClick
              }
              variant="secondary"
              icon="photo3dBold"
              className={styles.orderButton}
              disabled={
                isStorageBlocked || (isMobile && isForbidUploadsFromWeb)
              }
            >
              <FormattedMessage
                id="orderReport.dentalPhoto"
                defaultMessage="Photo"
              />
            </Button>,
          )}

        {shouldShowTranscriptorButton &&
          wrapWithStorageTooltip(
            <Button
              onClick={handleTranscriptorClick}
              variant="secondary"
              icon="orderScribe"
              className={styles.orderButton}
              disabled={isStartRecordingDisabled}
            >
              {renderCoinBadge(transcriptorFunding)}
              <FormattedMessage
                id="orderReport.transcriptor"
                defaultMessage="Transcriptor"
              />
            </Button>,
          )}

        {shouldShowNewDocumentButton && (
          <Button
            data-component="create-patient-document-button"
            onClick={handleNewDocumentClick}
            variant="secondary"
            icon="doc3d"
            className={styles.orderButton}
          >
            <FormattedMessage
              id="orderReport.newDocument"
              defaultMessage="Document"
            />
          </Button>
        )}
      </div>
    </>
  );
};
